import { Stack } from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ChecklistIcon from '@mui/icons-material/Checklist';
import BusinessIcon from '@mui/icons-material/Business';
import GradeIcon from '@mui/icons-material/Grade';

import ExecutiveSummaryCard from './review/ExecutiveSummaryCard';
import CategoryCard from './review/CategoryCard';
import ComplianceDetails from './review/ComplianceDetails';
import CompletenessDetails from './review/CompletenessDetails';
import AlignmentDetails from './review/AlignmentDetails';
import QualityDetails from './review/QualityDetails';

interface ComplianceCheck {
  passed: boolean;
  requiredDocuments: Array<{
    name: string;
    mandatory: boolean;
    present: boolean;
    location?: string;
  }>;
  missingMandatory: string[];
  missingOptional: string[];
}

interface CompletenessAnalysis {
  score: number;
  totalRequirements: number;
  addressedRequirements: number;
  partiallyAddressed: number;
  notAddressed: number;
  gaps: Array<{
    requirement: string;
    issue: string;
    severity: 'high' | 'medium' | 'low';
  }>;
}

interface OrganizationalAlignment {
  score: number;
  evidencedClaims: number;
  unsupportedClaims: number;
  contextDocumentsUsed: string[];
  alignmentIssues: Array<{
    claim: string;
    issue: string;
    suggestion: string;
  }>;
}

interface QualityAssessment {
  overallScore: number;
  relevanceScore: number;
  clarityScore: number;
  innovationScore: number;
  feasibilityScore: number;
  strengthAreas: string[];
  improvementAreas: string[];
}

interface InlineComment {
  section: string;
  text: string;
  comment: string;
  type: 'strength' | 'weakness' | 'suggestion' | 'missing' | 'question';
  priority: 'high' | 'medium' | 'low';
  category: 'compliance' | 'completeness' | 'alignment' | 'quality';
}

interface ReviewData {
  reviewId: string;
  overallScore: number;
  summary: string;
  strengths: string[];
  weaknesses: string[];
  suggestions: string[];
  compliance: ComplianceCheck;
  completeness: CompletenessAnalysis;
  organizationalAlignment: OrganizationalAlignment;
  quality: QualityAssessment;
  inlineComments: InlineComment[];
  timestamp: string;
}

interface ReviewViewerProps {
  reviewData: ReviewData;
}

export default function ReviewViewer({ reviewData }: ReviewViewerProps) {
  const getStatus = (score: number): 'pass' | 'warning' | 'fail' => {
    if (score >= 80) return 'pass';
    if (score >= 60) return 'warning';
    return 'fail';
  };

  return (
    <Stack spacing={3}>
      {/* Executive Summary - Always visible at top */}
      <ExecutiveSummaryCard
        score={reviewData.overallScore}
        summary={reviewData.summary}
        compliancePassed={reviewData.compliance.passed}
        strengths={reviewData.strengths}
        criticalIssues={reviewData.weaknesses}
        recommendations={reviewData.suggestions}
      />

      {/* Compliance Check Category */}
      <CategoryCard
        title="Compliance Check"
        score={reviewData.compliance.passed ? 100 : 0}
        status={reviewData.compliance.passed ? 'pass' : 'fail'}
        summary={`${reviewData.compliance.requiredDocuments.length} requirements checked`}
        details={<ComplianceDetails data={reviewData.compliance} />}
        defaultExpanded={!reviewData.compliance.passed}
        icon={<AssignmentTurnedInIcon color="primary" />}
      />

      {/* Completeness Analysis Category */}
      <CategoryCard
        title="Completeness Analysis"
        score={reviewData.completeness.score}
        status={getStatus(reviewData.completeness.score)}
        summary={`${reviewData.completeness.addressedRequirements}/${reviewData.completeness.totalRequirements} requirements addressed`}
        details={<CompletenessDetails data={reviewData.completeness} />}
        icon={<ChecklistIcon color="primary" />}
      />

      {/* Organizational Alignment Category */}
      <CategoryCard
        title="Organizational Alignment"
        score={reviewData.organizationalAlignment.score}
        status={getStatus(reviewData.organizationalAlignment.score)}
        summary={`${reviewData.organizationalAlignment.evidencedClaims} claims supported by evidence`}
        details={<AlignmentDetails data={reviewData.organizationalAlignment} />}
        icon={<BusinessIcon color="primary" />}
      />

      {/* Quality Assessment Category */}
      <CategoryCard
        title="Quality Assessment"
        score={reviewData.quality.overallScore}
        status={getStatus(reviewData.quality.overallScore)}
        summary="Relevance, clarity, innovation, and feasibility"
        details={<QualityDetails data={reviewData.quality} />}
        icon={<GradeIcon color="primary" />}
      />
    </Stack>
  );
}
